import React, { useEffect, useState } from 'react';
import { Activity, ChevronDown, ChevronUp, HeartPulse, AlertTriangle, Loader2, Check, X } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Rehab panel — browse and enrol in clinical rehab modules.
 *
 * MVP layout:
 *   - If NOT enrolled: show a compact list of available modules; tap one
 *     to open the enrol sheet (module detail + severity picker).
 *   - If ENROLLED: show current module summary, current phase's exercises
 *     with coaching cues, and controls to progress phase or unenrol.
 *
 * Sided modules (e.g. bilateral shoulder impingement) accept different
 * severities per side. Non-sided modules use a single severity.
 */
const SEVERITY_OPTIONS = [
  { id: 'mild',     label: 'Mild' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'severe',   label: 'Severe' },
];

const SEVERITY_COPY = {
  mild:     'Bearable, doesn\u2019t stop your day. Some overhead work still possible.',
  moderate: 'Limits daily use; overhead pressing and deep pressing are painful.',
  severe:   'Painful at rest or with any load. Clinical assessment recommended.',
};

export default function RehabPanel({ testid = 'rehab-panel' }) {
  const [modules, setModules] = useState([]);
  const [enrolment, setEnrolment] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [phaseBusy, setPhaseBusy] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // module currently being enrolled

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [mods, enr] = await Promise.all([api.rehabModules(), api.rehabEnrolment()]);
        if (!alive) return;
        setModules(mods.modules || []);
        if (enr.enrolled) {
          setEnrolment(enr.enrolment);
          setActiveModule(enr.module);
        }
      } catch { /* stay closed */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const doEnrol = async (module_id, severity, left, right) => {
    setEnrolBusy(true);
    try {
      const res = await api.rehabEnrol(module_id, severity, left, right);
      setEnrolment(res.enrolment);
      setActiveModule(res.module);
      setPickerFor(null);
      setExpanded(true);
    } catch (e) {
      alert(e?.message || 'Could not enrol');
    } finally { setEnrolBusy(false); }
  };

  const doProgress = async (to_phase) => {
    setPhaseBusy(true);
    try {
      const res = await api.rehabProgress(to_phase);
      setEnrolment(res.enrolment);
      setActiveModule(res.module);
    } catch (e) { alert(e?.message || 'Try again'); }
    finally { setPhaseBusy(false); }
  };

  const doUnenrol = async () => {
    if (!window.confirm('End this rehab programme? Your exercise restrictions will lift.')) return;
    try {
      await api.rehabUnenrol();
      setEnrolment(null);
      setActiveModule(null);
    } catch (e) { alert(e?.message || 'Try again'); }
  };

  if (loading) {
    return <div className="text-xs text-zinc-500 py-3" data-testid={`${testid}-loading`}>Loading rehab modules…</div>;
  }

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        <div className={`h-9 w-9 rounded-xl grid place-items-center ${enrolment ? 'bg-[#BEFF38]/10 text-[#BEFF38]' : 'bg-zinc-800 text-zinc-400'}`}>
          <HeartPulse className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">Rehab programmes</div>
          <div className="text-[11px] text-zinc-400 mt-0.5 leading-tight">
            {enrolment
              ? `Enrolled: ${activeModule?.short_name || activeModule?.name} · ${enrolment.current_phase}`
              : 'Clinical programmes that adapt your training around a specific injury'}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-3">
          {!enrolment && (
            <>
              <p className="text-[11px] text-zinc-500 leading-snug">
                Enrolling in a rehab module applies a temporary exercise restriction matrix on top of your normal plan. Your Daily Call and Routines will avoid or substitute contraindicated movements until you unenrol.
              </p>
              {modules.length === 0 && (
                <p className="text-zinc-500 text-sm">No modules available yet.</p>
              )}
              {modules.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setPickerFor(m)}
                  data-testid={`${testid}-module-${m.id}`}
                  className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-950 hover:border-zinc-700 p-3 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-white flex-1">{m.name}</p>
                    <span className="text-[9px] uppercase tracking-wider font-bold text-[#BEFF38] bg-[#BEFF38]/10 border border-[#BEFF38]/30 rounded-full px-1.5 py-0.5">
                      {m.phase_count}-phase
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1 leading-snug line-clamp-2">
                    {(m.presentation || [])[0] || ''}
                  </p>
                </button>
              ))}
            </>
          )}

          {enrolment && activeModule && (
            <EnroledView
              module={activeModule}
              enrolment={enrolment}
              onProgress={doProgress}
              onUnenrol={doUnenrol}
              phaseBusy={phaseBusy}
              testid={testid}
            />
          )}
        </div>
      )}

      {pickerFor && (
        <EnrolSheet
          module={pickerFor}
          onClose={() => setPickerFor(null)}
          onConfirm={doEnrol}
          busy={enrolBusy}
        />
      )}
    </div>
  );
}


function EnroledView({ module, enrolment, onProgress, onUnenrol, phaseBusy, testid }) {
  const currentPhase = (module.phases || []).find((p) => p.id === enrolment.current_phase) || (module.phases || [])[0];
  const phaseIdx = (module.phases || []).findIndex((p) => p.id === enrolment.current_phase);
  const canBack = phaseIdx > 0;
  const canFwd  = phaseIdx >= 0 && phaseIdx < (module.phases || []).length - 1;

  const severityBits = [];
  if (module.sided) {
    if (enrolment.left_severity)  severityBits.push(`Left: ${enrolment.left_severity}`);
    if (enrolment.right_severity) severityBits.push(`Right: ${enrolment.right_severity}`);
  } else {
    severityBits.push(enrolment.severity);
  }

  const isSevere = enrolment.severity === 'severe' || enrolment.left_severity === 'severe' || enrolment.right_severity === 'severe';

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-[#BEFF38]/30 bg-[#BEFF38]/5 p-3">
        <p className="text-[9px] uppercase tracking-wider text-[#BEFF38] font-black">Currently enrolled</p>
        <p className="text-white font-semibold text-sm mt-0.5">{module.name}</p>
        <p className="text-[11px] text-zinc-400 mt-1">Severity — {severityBits.join(' · ')}</p>
      </div>

      {isSevere && module.clinical_signposting && (
        <div className="rounded-xl border border-orange-500/40 bg-orange-500/10 p-3 flex gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-300 shrink-0 mt-0.5" />
          <p className="text-[11px] text-orange-100 leading-snug">{module.clinical_signposting}</p>
        </div>
      )}

      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div>
            <p className="text-[9px] uppercase tracking-wider text-[#ff7a1a] font-black">Current phase</p>
            <p className="text-white font-semibold text-sm mt-0.5">{currentPhase?.label}</p>
            {currentPhase?.weeks && <p className="text-[10px] text-zinc-500 mt-0.5">Approx. weeks {currentPhase.weeks}</p>}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onProgress((module.phases || [])[phaseIdx - 1]?.id)}
              disabled={!canBack || phaseBusy}
              data-testid={`${testid}-phase-back`}
              className="h-7 px-2 rounded-full text-[10px] font-black uppercase tracking-wider bg-zinc-800 text-zinc-200 disabled:opacity-40 hover:bg-zinc-700"
            >
              Back
            </button>
            <button
              onClick={() => onProgress((module.phases || [])[phaseIdx + 1]?.id)}
              disabled={!canFwd || phaseBusy}
              data-testid={`${testid}-phase-next`}
              className="h-7 px-2 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#BEFF38] text-black disabled:opacity-40 hover:bg-lime-300"
            >
              {phaseBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Next phase'}
            </button>
          </div>
        </div>
        {currentPhase?.intro && <p className="text-[11px] text-zinc-300 leading-snug mt-1">{currentPhase.intro}</p>}
        {currentPhase?.exercises?.length > 0 && (
          <ul className="mt-3 space-y-2">
            {currentPhase.exercises.map((ex, i) => (
              <li key={i} className="rounded-lg bg-zinc-900 border border-zinc-800 p-2.5" data-testid={`${testid}-exercise-${i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm text-white font-medium">{ex.name}</p>
                  <p className="text-[10px] text-zinc-500 shrink-0">{ex.prescription}</p>
                </div>
                <p className="text-[10px] text-zinc-500 mt-0.5">{ex.purpose}</p>
                {ex.coaching && (
                  <p className="text-[11px] text-[#BEFF38]/80 mt-1.5 leading-snug border-l border-[#BEFF38]/30 pl-2">{ex.coaching}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {module.coaching_cue_overrides?.scapular_alignment && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-black">Scapular coaching cue</p>
          <p className="text-[11px] text-zinc-300 mt-1 leading-snug italic">
            {module.coaching_cue_overrides.scapular_alignment}
          </p>
        </div>
      )}

      <button
        onClick={onUnenrol}
        data-testid={`${testid}-unenrol`}
        className="text-[11px] uppercase tracking-wider font-black text-zinc-400 hover:text-white"
      >
        End programme
      </button>
    </div>
  );
}


function EnrolSheet({ module, onClose, onConfirm, busy }) {
  const [severity, setSeverity] = useState('moderate');
  const [left, setLeft]   = useState('moderate');
  const [right, setRight] = useState('moderate');

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
      onClick={onClose}
      data-testid="rehab-enrol-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-5 text-white max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[#BEFF38] text-[11px] uppercase tracking-wider font-black">Rehab module</p>
            <h3 className="text-lg font-bold mt-0.5">{module.name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="rehab-enrol-close"
            className="h-8 w-8 rounded-full grid place-items-center bg-zinc-800 hover:bg-zinc-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {(module.presentation || []).length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-black mb-1">Designed for</p>
            <ul className="text-[12px] text-zinc-300 space-y-0.5 leading-snug">
              {module.presentation.map((p, i) => <li key={i}>• {p}</li>)}
            </ul>
          </div>
        )}

        {module.sided ? (
          <div className="space-y-3 mt-4">
            <SeverityPicker label="Left side severity"  value={left}  onChange={setLeft}  testid="rehab-severity-left" />
            <SeverityPicker label="Right side severity" value={right} onChange={setRight} testid="rehab-severity-right" />
          </div>
        ) : (
          <div className="mt-4">
            <SeverityPicker label="Severity" value={severity} onChange={setSeverity} testid="rehab-severity" />
          </div>
        )}

        <button
          onClick={() => onConfirm(module.id, severity, module.sided ? left : null, module.sided ? right : null)}
          disabled={busy}
          data-testid="rehab-enrol-confirm"
          className="mt-5 w-full h-12 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Enrol
        </button>
      </div>
    </div>
  );
}


function SeverityPicker({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-black mb-1.5">{label}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {SEVERITY_OPTIONS.map((s) => {
          const on = value === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onChange(s.id)}
              data-testid={`${testid}-${s.id}`}
              className={`h-10 rounded-xl text-xs font-semibold transition-colors ${
                on
                  ? 'bg-[#BEFF38] text-black'
                  : 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">{SEVERITY_COPY[value]}</p>
    </div>
  );
}
